import React from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text/Text'
import Button from 'core-system/Button'
import palette from 'core-system/Themes/palette'
import { ComplianceData } from '../adminDashboardTypes'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'

const Container = styled.div`
  background-color: ${(props) => props.theme.palette.white};
  border: 1px solid ${(props) => props.theme.palette.grey.grey300};
  border-radius: 0.75rem;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  height: 100%;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
`

const Title = styled(Text)`
  font-weight: 600;
`

const ViewDetailsLink = styled.button`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  background: none;
  border: none;
  color: ${(props) => props.theme.palette.text.secondary};
  font-size: 0.8125rem;
  cursor: pointer;

  &:hover {
    color: ${(props) => props.theme.palette.text.primary};
  }
`

const ContentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 1.25rem;
`

const DonutContainer = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
`

const DonutSvg = styled.svg`
  transform: rotate(-90deg);
`

const DonutCenter = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
`

const DonutValue = styled(Text)`
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1;
`

const LegendContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const LegendDot = styled.div<{ $color: string }>`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
`

const LegendText = styled(Text)`
  font-size: 0.875rem;
`

const AlertBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background-color: ${palette.warning.yellow50};
  border: 1px solid ${palette.warning.yellow200};
  border-radius: 0.5rem;
  margin-top: auto;
`

const AlertContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const AlertIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  background-color: ${palette.warning.yellow400};
  color: white;
  flex-shrink: 0;
`

const AlertTextContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const AlertTitle = styled(Text)`
  font-weight: 600;
  font-size: 0.875rem;
`

const AlertSubtitle = styled(Text)`
  color: ${palette.text.secondary};
  font-size: 0.75rem;
`

const ReviewButton = styled(Button)`
  background-color: ${palette.primary.pink800};
  color: white;
  padding: 0.5rem 1rem;
  font-size: 0.8125rem;

  &:hover {
    background-color: ${palette.primary.pink900};
  }
`

interface ComplianceWidgetProps {
  data: ComplianceData
}

const ComplianceWidget = ({ data }: ComplianceWidgetProps) => {
  const compliantPercent = (data.compliantSites / data.totalSites) * 100
  const nonCompliantSites = data.totalSites - data.compliantSites

  // SVG donut chart calculations
  const radius = 35
  const circumference = 2 * Math.PI * radius
  const compliantOffset =
    circumference - (compliantPercent / 100) * circumference

  return (
    <Container>
      <Header>
        <Title variant='body1'>Compliance Overview</Title>
        <ViewDetailsLink>
          View Details <ArrowForwardIcon style={{ fontSize: '0.875rem' }} />
        </ViewDetailsLink>
      </Header>

      <ContentRow>
        <DonutContainer>
          <DonutSvg width='80' height='80' viewBox='0 0 80 80'>
            {/* Background circle (non-compliant) */}
            <circle
              cx='40'
              cy='40'
              r={radius}
              fill='none'
              stroke={palette.error.red300}
              strokeWidth='8'
            />
            {/* Foreground circle (compliant) */}
            <circle
              cx='40'
              cy='40'
              r={radius}
              fill='none'
              stroke={palette.success.green500}
              strokeWidth='8'
              strokeDasharray={circumference}
              strokeDashoffset={compliantOffset}
              strokeLinecap='round'
            />
          </DonutSvg>
          <DonutCenter>
            <DonutValue>
              {data.compliantSites}/{data.totalSites}
            </DonutValue>
          </DonutCenter>
        </DonutContainer>

        <LegendContainer>
          <LegendItem>
            <LegendDot $color={palette.success.green500} />
            <LegendText fontWeight={600}>Sites Compliant</LegendText>
          </LegendItem>
          <LegendItem>
            <LegendDot $color={palette.success.green500} />
            <LegendText color={palette.success.green600}>
              {data.compliantSites} compliant
            </LegendText>
          </LegendItem>
          <LegendItem>
            <LegendDot $color={palette.error.red400} />
            <LegendText color={palette.error.red500}>
              {nonCompliantSites} action needed
            </LegendText>
          </LegendItem>
        </LegendContainer>
      </ContentRow>

      {data.alerts.length > 0 && (
        <AlertBanner>
          <AlertContent>
            <AlertIconWrapper>
              <WarningAmberIcon style={{ fontSize: '0.875rem' }} />
            </AlertIconWrapper>
            <AlertTextContainer>
              <AlertTitle>
                {data.alerts[0].siteName} site requires attention
              </AlertTitle>
              <AlertSubtitle>{data.alerts[0].message}</AlertSubtitle>
            </AlertTextContainer>
          </AlertContent>
          <ReviewButton variant='primary' size='small'>
            Review
          </ReviewButton>
        </AlertBanner>
      )}
    </Container>
  )
}

export default React.memo(ComplianceWidget)
